// frontend/src/components/Layout.js
import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import io from 'socket.io-client';
import {
  Home,
  Search,
  Users,
  Bell,
  User,
  Settings,
  LogOut,
  BookOpen, Timer, Target, FileText,
  SlidersHorizontal,
  Award,
  BarChart3,
  Brain,
  Sparkles
} from 'lucide-react';
import './Layout.css';

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Notification badge state
  const [unreadCount, setUnreadCount] = useState(0);
  const [socket, setSocket] = useState(null);

  // Fetch unread count function
  const fetchUnreadCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get('/api/notifications/unread-count', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.unreadCount || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, []);

  // Fetch unread count on mount and when user changes
  useEffect(() => {
    if (user) {
      fetchUnreadCount();
    }
  }, [user, fetchUnreadCount]);

  // Set up socket connection for real-time notifications
  useEffect(() => {
    if (user) {
      // Connect to the same origin (production URL)
      const newSocket = io(window.location.origin, {
        transports: ['websocket', 'polling'],
        auth: {
          token: localStorage.getItem('token')
        }
      });

      newSocket.on('connect', () => {
        console.log('Layout socket connected');
        newSocket.emit('authenticate', user.userId || user._id);
      });

      // Listen for new notifications
      newSocket.on('notification', (notification) => {
        console.log('New notification received:', notification);
        setUnreadCount(prev => prev + 1);
      });

      // Listen for notification count updates
      newSocket.on('notificationCountUpdate', (data) => {
        if (data && typeof data.unreadCount === 'number') {
          setUnreadCount(data.unreadCount);
        }
      });

      setSocket(newSocket);

      return () => {
        newSocket.disconnect();
      };
    }
  }, [user]);

  // Refetch count when navigating to notifications page
  useEffect(() => {
    if (location.pathname === '/notifications') {
      fetchUnreadCount();
    }
  }, [location.pathname, fetchUnreadCount]);

  const handleLogout = async () => {
    if (socket) {
      socket.disconnect();
    }
    await logout();
    navigate('/auth/login');
  };

  const navigationItems = [
    { path: '/home', icon: Home, label: 'Home' },

        {
          label: 'Study Rooms',
          path: '/study-rooms',
          icon: Users || null,
        },
    { path: '/search', icon: Search, label: 'Search' },
    { path: '/communities', icon: Users, label: 'Communities' },
    { path: '/notifications', icon: Bell, label: 'Notifications', showBadge: true },
    { path: '/smart-study', icon: Brain, label: 'Smart Study' },
    { path: '/analytics', icon: BarChart3, label: 'Analytics' },
    { path: '/study-session', icon: SlidersHorizontal, label: 'Practice' },
    { path: '/certificates', icon: Award, label: 'Certificates' },
    { path: '/study-plan', icon: BookOpen, label: 'Study Plan' },
    { path: '/exam-countdown', icon: Timer, label: 'Exam Prep' },
    { path: '/goals', icon: Target, label: 'Goals' },
    { path: '/weekly-reports', icon: FileText, label: 'Reports' },
  ];

  return (
    <div className="layout-container">
      {/* Sidebar Navigation */}
      <nav className="sidebar">
        <div className="sidebar-header">
          <BookOpen size={32} className="logo" />
          <h1 className="brand">MathZA</h1>
        </div>

        <div className="nav-items">
          {navigationItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `nav-item ${isActive ? 'active' : ''}`
              }
            >
              <div className="nav-icon-wrapper">
                <item.icon size={20} />
                {item.showBadge && unreadCount > 0 && (
                  <span className="notification-badge">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </div>
              <span>{item.label}</span>
            </NavLink>
          ))}

          {/* Educator Dashboard Link (if user is educator) */}
          {user?.role === 'educator' && (
            <NavLink
              to="/educator/dashboard"
              className={({ isActive }) =>
                `nav-item ${isActive ? 'active' : ''}`
              }
            >
              <BookOpen size={20} />
              <span>Educator Dashboard</span>
            </NavLink>
          )}
        </div>

        <div className="sidebar-footer">
          <NavLink
            to="/pricing"
            className={({ isActive }) =>
              `nav-item upgrade-link ${isActive ? 'active' : ''}`
            }
          >
            <Sparkles size={20} />
            <span>Upgrade</span>
          </NavLink>
          <NavLink
            to={`/profile/${user?.username}`}
            className={({ isActive }) =>
              `nav-item ${isActive ? 'active' : ''}`
            }
          >
            <User size={20} />
            <span>Profile</span>
          </NavLink>

          <NavLink
            to="/settings"
            className={({ isActive }) =>
              `nav-item ${isActive ? 'active' : ''}`
            }
          >
            <Settings size={20} />
            <span>Settings</span>
          </NavLink>

          <button onClick={handleLogout} className="nav-item logout-btn">
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>

        {/* User Info */}
        <div className="user-info">
          <div className="user-avatar">
            {user?.avatar ? (
              <img src={user.avatar} alt={user.username} />
            ) : (
              <div className="avatar-placeholder">
                {user?.username?.[0]?.toUpperCase() || 'U'}
              </div>
            )}
          </div>
          <div className="user-details">
            <div className="username">@{user?.username}</div>
            <div className="user-grade">Grade {user?.grade}</div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="main-content">
        <Outlet context={{ refreshNotificationCount: fetchUnreadCount }} />
      </main>
    </div>
  );
};

export default Layout;
